import { useState, useEffect, useRef } from 'react';
import { contactsAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { User, ChevronDown } from 'lucide-react';

/**
 * Selector de contactos con opción de texto libre (creatable).
 * value: { contact_id?, name, email?, phone?, channel? }
 */
export default function ContactPicker({ value, onChange, placeholder = 'Nombre o buscar contacto...', testId }) {
  const [query, setQuery] = useState(value?.name || '');
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    setQuery(value?.name || '');
  }, [value?.contact_id, value?.name]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const search = async (q) => {
    setLoading(true);
    try {
      const res = await contactsAPI.list({ q: q || undefined, limit: 20 });
      setContacts(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFocus = () => {
    setOpen(true);
    search(query);
  };

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    search(v);
    onChange({
      contact_id: null,
      name: v,
      email: value?.email || '',
      phone: value?.phone,
      channel: value?.channel,
    });
  };

  const pickContact = (c) => {
    const primary = (c.identities || []).find((i) => i.is_primary) || (c.identities || [])[0];
    const email = primary?.raw_value?.includes('@') ? primary.raw_value : (value?.email || '');
    onChange({
      contact_id: c.contact_id,
      name: c.display_name || c.contact_id,
      email,
      phone: primary?.channel_type === 'whatsapp' ? primary.raw_value : undefined,
      channel: primary?.channel_type,
    });
    setQuery(c.display_name || '');
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <User className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" />
        <Input
          data-testid={testId}
          value={query}
          onChange={handleInput}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="pl-7 pr-7 h-8 text-xs rounded-sm"
        />
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400 pointer-events-none" />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-brand-border rounded-sm shadow-lg max-h-48 overflow-auto">
          {loading && <div className="p-2 text-[10px] text-zinc-400">Buscando...</div>}
          {!loading && contacts.length === 0 && query && (
            <div className="p-2 text-[10px] text-zinc-500">
              Sin coincidencias — se guardará como texto libre: <strong>{query}</strong>
            </div>
          )}
          {contacts.map((c) => (
            <button
              key={c.contact_id}
              type="button"
              className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 border-b border-zinc-50 last:border-0"
              onClick={() => pickContact(c)}
            >
              <span className="font-medium">{c.display_name}</span>
              {c.tags?.length > 0 && (
                <span className="ml-2 text-[10px] text-zinc-400">{c.tags.join(', ')}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
